// Analytics/services/endpoints/inpatients.js
import axios from 'axios';
import { datalogger } from '../../../../../utils/index.jsx';
import { shouldServeMock } from '../demoMock';

const ANALYTICS_BASE_URL = import.meta.env.VITE_APP_API_URL + "/analytics";

// ============================================
// MOCK CONFIGURATION
// ============================================
const MOCK_CONFIG = {
    enabled: import.meta.env.VITE_MOCK_INPATIENTS === 'true',
    endpoints: {
        'readmission-rate': true,
        'pharmacy-usage': true,
        'admissions-by-department': true,
        'indicators-by-department': true,
        'discharge-diagnoses': true,
        'current-situation': true,
        'period-activity': true,
    },
    delay: 300
};

// ============================================
// MOCK DATA GENERATORS
// ============================================

const getMockReadmissionRateResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Readmission Rate", data: [8.5, 9.2, 7.8, 10.1, 8.9, 9.5, 10.3] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 9.2
    }
});

const getMockPharmacyUsageResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Pharmacy Usage", data: [120, 135, 128, 142, 138, 145, 150] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 137
    }
});

const getMockAdmissionsByDepartmentResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Admissions", data: [45, 30, 25, 20] }
        ],
        categories: ["Cardiology", "Neurology", "Orthopedics", "Pediatrics"],
        count: 120
    }
});

const getMockIndicatorsByDepartmentResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Indicators", data: [45, 30, 25, 20] }
        ],
        categories: ["Cardiology", "Neurology", "Orthopedics", "Pediatrics"],
        count: 120
    }
});

const getMockCurrentSituationResponse = () => ({
    status: 200,
    stats: {
        totals: {
            currentPatients: 42,
            occupiedBeds: 38,
            totalCapacity: 50,
            occupancyRate: 76,
            averageCurrentLosDays: 5.2,
            patientsOver30Days: 4,
        },
        byDepartment: [
            {
                departmentUuid: 'dept-uuid-1',
                departmentName: 'Service de Médecine',
                currentPatients: 24,
                occupiedBeds: 22,
                availableBeds: 3,
                totalCapacity: 25,
                occupancyRate: 88,
                averageCurrentLosDays: 6.1,
                patientsOver7Days: 8,
                patientsOver14Days: 4,
                patientsOver30Days: 2,
            },
            {
                departmentUuid: 'dept-uuid-2',
                departmentName: 'Service de Chirurgie',
                currentPatients: 18,
                occupiedBeds: 16,
                availableBeds: 9,
                totalCapacity: 25,
                occupancyRate: 64,
                averageCurrentLosDays: 3.8,
                patientsOver7Days: 3,
                patientsOver14Days: 1,
                patientsOver30Days: 2,
            },
        ],
    },
});

const getMockPeriodActivityResponse = () => ({
    status: 200,
    stats: {
        totals: {
            admissions: 120,
            discharges: 105,
            deaths: 3,
            medianLosDays: 4.5,
            mortalityRate: 2.9,
            // Fewer than the 120 admissions: a demo where the two coincided would hide the
            // distinction the tile exists to show.
            uniquePatients: 96,
        },
        // SHER-897. Without this block a demo investigation renders the demographics card as an
        // error — `demographicsState` reads a missing population as "no answer", which is what
        // it is. SHER-896 added the equivalent to the outpatient mock for the same reason.
        patients: {
            ids: Array.from({ length: 96 }, (_, index) => index + 1),
            firstAdmissionDates: Array.from({ length: 96 }, (_, index) => index + 1).reduce(
                (dates, id) => ({
                    ...dates,
                    [id]: `${new Date().getFullYear()}-${String((id % 12) + 1).padStart(2, '0')}-${String((id % 27) + 1).padStart(2, '0')}`,
                }),
                {},
            ),
        },
        byDepartment: [
            {
                departmentUuid: 'dept-uuid-1',
                departmentName: 'Service de Médecine',
                admissions: 70,
                discharges: 60,
                deaths: 2,
                medianLosDays: 5,
                mortalityRate: 3.3,
                averageOccupancyRate: 78,
            },
            {
                departmentUuid: 'dept-uuid-2',
                departmentName: 'Service de Chirurgie',
                admissions: 50,
                discharges: 45,
                deaths: 1,
                medianLosDays: 3,
                mortalityRate: 2.2,
                averageOccupancyRate: 65,
            },
        ],
        chart: {
            bucket: 'week',
            categories: ['2026-08-01', '2026-08-08'],
            series: [
                { name: 'admissions', data: [55, 65] },
                { name: 'discharges', data: [48, 57] },
                { name: 'deaths', data: [1, 2] },
            ],
        },
    },
});

// Mock registry
const getMockDischargeDiagnosesResponse = () => ({
    status: 200,
    stats: {
        diagnoses: [
            // SHER-583: patientIds/patientsLength feed the Female/Male/Total columns. Fewer
            // patients than discharges on purpose — readmissions are why the table carries both.
            { code: 'BA00', name: 'Acute rheumatic fever', discharges: 18, departments: ['Service de Médecine', 'Service de Pédiatrie'], patientIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], patientsLength: 15 },
            { code: 'CA40', name: 'Pneumonia', discharges: 14, departments: ['Service de Médecine'], patientIds: [16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27], patientsLength: 12 },
            { code: '1F40', name: 'Malaria', discharges: 11, departments: ['Service de Médecine', 'Service de Chirurgie', 'Service de Pédiatrie'], patientIds: [28, 29, 30, 31, 32, 33, 34, 35, 36, 37], patientsLength: 10 },
            { code: 'DA60', name: 'Peptic ulcer', discharges: 6, departments: ['Service de Chirurgie'], patientIds: [38, 39, 40, 41, 42], patientsLength: 5 },
        ],
        totalDischarges: 49,
    },
});

const MOCK_REGISTRY = {
    'readmission-rate': getMockReadmissionRateResponse,
    'pharmacy-usage': getMockPharmacyUsageResponse,
    'admissions-by-department': getMockAdmissionsByDepartmentResponse,
    'indicators-by-department': getMockIndicatorsByDepartmentResponse,
    'discharge-diagnoses': getMockDischargeDiagnosesResponse,
    'current-situation': getMockCurrentSituationResponse,
    'period-activity': getMockPeriodActivityResponse,
};

const getAuthHeaders = () => ({
    headers: { "Authorization": localStorage.getItem("jwt") }
});

const buildOutpatientsUrl = (endpoint, { uuidInvestigation, uuidDepartment, startDate, endDate }) => {
    const baseUrl = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/inpatients`;
    const endpointPath = endpoint || '';
    if (endpointPath === 'current-situation') {
        return `${baseUrl}/${endpointPath}/${uuidDepartment}`;
    }
    console.log('Building URL: inpatients', endpointPath, uuidDepartment, startDate, endDate);
    return `${baseUrl}/${endpointPath}/${uuidDepartment}/startDate/${startDate}/endDate/${endDate}`;
};

const makeOutpatientsRequest = async (params, signal) => {
    const { endpointPath } = params;
    const url = buildOutpatientsUrl(endpointPath, params);
    console.log('Making request to:', params, endpointPath);
    
    // Return mock data if enabled
    if (shouldServeMock(endpointPath, MOCK_CONFIG, MOCK_REGISTRY)) {
        console.log(`[MOCK] Returning mock data for: ${endpointPath}`);
        await new Promise(resolve => setTimeout(resolve, MOCK_CONFIG.delay));
        return MOCK_REGISTRY[endpointPath]();
    }
    
    try {
        const response = await axios.get(url, {
            ...getAuthHeaders(),
            signal
        });
        if (response.status === 200) {
            console.log('Inpatients API Response:', endpointPath, response.data);
            return response.data;
        }
    } catch (error) {
        console.error('Error in inp:', endpointPath, error);
    }
};

const createEndpoint = (endpointPath) => {
    console.log('Creating inpatients endpoint:', endpointPath);
    return datalogger((uuidInvestigation, uuidDepartment, startDate, endDate, signal) => 
        makeOutpatientsRequest({ 
            endpointPath,
            uuidInvestigation, 
            uuidDepartment, 
            startDate, 
            endDate 
        }, signal)
    );
};

const createLiveEndpoint = (endpointPath) => {
    return datalogger((uuidInvestigation, uuidDepartment, signal) =>
        makeOutpatientsRequest({
            endpointPath,
            uuidInvestigation,
            uuidDepartment,
        }, signal)
    );
};

export const inpatientsServices = {
    getReadmissionRate: createEndpoint('readmission-rate'),
    getPharmacyUsage: createEndpoint('pharmacy-usage'),
    getAdmissionsByDepartment: createEndpoint('admissions-by-department'),
    getIndicatorsByDepartment: createEndpoint('indicators-by-department'),
    getDischargeDiagnoses: createEndpoint('discharge-diagnoses'),
    getCurrentSituation: createLiveEndpoint('current-situation'),
    getPeriodActivity: createEndpoint('period-activity'),
};
